/**
 * AI-OCR HITL レビュー画面。
 * 左: ページ一覧、中央: 選択ページの画像プレビュー、右: 抽出結果の編集可能な行一覧。
 * 承認/修正/スキップ後、会社管理者が「確定」を押すと delivery_details へ反映される。
 * 詳細: docs/design/v02-ai-ocr-web-integration.md §3-4
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, X, Pencil, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { ConfirmDialog } from '@/components/Modal';

type OcrJobStatus = 'uploaded' | 'queued' | 'processing' | 'pending_review' | 'completed' | 'failed';
type OcrJobDetail = {
  job_id: string;
  store_id: string;
  target_month: string;
  status: OcrJobStatus;
  page_count: number | null;
  error_message: string | null;
  pages: Array<{ page_id: string; page_number: number; vendor_name_raw: string | null; status: string }>;
};

type Confidence = 'high' | 'medium' | 'low';
type OcrResult = {
  result_id: string;
  line_number: number;
  delivery_date: string | null;
  vendor_name: string | null;
  ingredient_name_raw: string | null;
  ingredient_name: string | null;
  ingredient_id: string | null;
  cost_category_id: string | null;
  unit_price: number | null;
  quantity: number | null;
  amount_tax_included: number | null;
  confidence: Confidence;
  issues: string[];
  hitl_decision: 'approved' | 'corrected' | 'skipped' | null;
  hitl_corrected_value: Record<string, unknown> | null;
  delivery_detail_id: string | null;
};
type PageResults = { page_id: string; page_number: number; status: string; results: OcrResult[] };

type Ingredient = { ingredient_id: string; store_id: string; ingredient_code: string; ingredient_name: string };

const CONFIDENCE_LABEL: Record<Confidence, string> = { high: '高', medium: '中', low: '低' };
// デザインレビュー反映 (2026-08-02): KpiCard.tsx と同じ warning-bg/warning-text トークンに統一。
// low は warning よりさらに強い注意が必要なため brand-red を充てる (high<medium<low の色の強さで段階を表現)
const CONFIDENCE_CLASS: Record<Confidence, string> = {
  high: 'bg-success-bg text-success-text',
  medium: 'bg-warning-bg text-warning-text',
  low: 'bg-brand-red/10 text-brand-red',
};
const CONFIDENCE_ICON: Record<Confidence, typeof AlertTriangle | null> = {
  high: CheckCircle2,
  medium: AlertTriangle,
  low: AlertTriangle,
};

const authFetchJson = async <T,>(url: string, init: RequestInit): Promise<T> => {
  const token = localStorage.getItem('access_token');
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  handleUnauthorized(res.status);
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
  }
  return body.data as T;
};

function PageImage({ jobId, pageId }: { jobId: string; pageId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let revoked = '';
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/ai/ocr/jobs/${jobId}/pages/${pageId}/image`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      revoked = url;
      if (!cancelled) setSrc(url);
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [jobId, pageId]);

  if (!src) return <div className="flex items-center justify-center h-full text-zinc-400"><Loader2 className="animate-spin" /></div>;
  return <img src={src} alt="納品書ページ" className="max-w-full h-auto mx-auto" />;
}

function EditableRow({
  row, ingredients, onSave, onDecision, saving,
}: {
  row: OcrResult;
  ingredients: Ingredient[];
  onSave: (resultId: string, correctedValue: Record<string, unknown>) => void;
  onDecision: (resultId: string, decision: 'approved' | 'skipped') => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const c = row.hitl_corrected_value ?? {};
  const [deliveryDate, setDeliveryDate] = useState(String(c.delivery_date ?? row.delivery_date ?? ''));
  const [vendorName, setVendorName] = useState(String(c.vendor_name ?? row.vendor_name ?? ''));
  const [ingredientId, setIngredientId] = useState(String(c.ingredient_id ?? row.ingredient_id ?? ''));
  const [unitPrice, setUnitPrice] = useState(String(c.unit_price ?? row.unit_price ?? ''));
  const [quantity, setQuantity] = useState(String(c.quantity ?? row.quantity ?? ''));
  const [amount, setAmount] = useState(String(c.amount_tax_included ?? row.amount_tax_included ?? ''));

  const locked = Boolean(row.delivery_detail_id);
  const decided = row.hitl_decision !== null;

  const save = () => {
    if (!deliveryDate) { toast.error('納品日は必須です'); return; }
    if (!ingredientId) { toast.error('食材を選択してください'); return; }
    onSave(row.result_id, {
      delivery_date: deliveryDate,
      vendor_name: vendorName || null,
      ingredient_id: ingredientId,
      unit_price: unitPrice ? Number(unitPrice) : null,
      quantity: quantity ? Number(quantity) : null,
      amount_tax_included: amount ? Number(amount) : null,
    });
    setEditing(false);
  };

  const ConfidenceIcon = CONFIDENCE_ICON[row.confidence];

  return (
    <div className={`p-3 rounded-lg border ${locked ? 'border-success-border bg-success-bg/40' : 'border-zinc-100'} space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${CONFIDENCE_CLASS[row.confidence]}`}>
          {ConfidenceIcon && <ConfidenceIcon size={12} />}
          確信度: {CONFIDENCE_LABEL[row.confidence]}
        </span>
        {locked && <span className="text-xs text-success-text flex items-center gap-1"><CheckCircle2 size={14} /> 確定済み</span>}
        {!locked && decided && (
          <span className="text-xs text-zinc-500">
            {row.hitl_decision === 'approved' ? '承認済み' : row.hitl_decision === 'corrected' ? '修正済み' : 'スキップ'}
          </span>
        )}
      </div>

      {row.issues.length > 0 && (
        <ul className="text-xs text-warning-text space-y-0.5">
          {row.issues.map((iss, i) => (
            <li key={i} className="flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{iss}</span>
            </li>
          ))}
        </ul>
      )}

      {!editing ? (
        <div className="text-sm grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
          <div><span className="text-zinc-400">納品日: </span>{row.delivery_date ?? '-'}</div>
          <div><span className="text-zinc-400">発行元: </span>{row.vendor_name ?? '-'}</div>
          <div><span className="text-zinc-400">品目: </span>{row.ingredient_name ?? `(未一致) ${row.ingredient_name_raw ?? ''}`}</div>
          <div><span className="text-zinc-400">単価: </span>{row.unit_price ?? '-'}</div>
          <div><span className="text-zinc-400">数量: </span>{row.quantity ?? '-'}</div>
          <div><span className="text-zinc-400">税込金額: </span>{row.amount_tax_included ?? '-'}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label className="text-xs text-zinc-500">納品日
            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-zinc-500">発行元
            <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-zinc-500 col-span-2 sm:col-span-1">食材
            <select value={ingredientId} onChange={(e) => setIngredientId(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm">
              <option value="">選択してください</option>
              {ingredients.map((i) => <option key={i.ingredient_id} value={i.ingredient_id}>{i.ingredient_name} ({i.ingredient_code})</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-500">単価
            <input type="number" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-zinc-500">数量
            <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-zinc-500">税込金額
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-0.5 w-full border border-neutral-line rounded px-2 py-1 text-sm" />
          </label>
        </div>
      )}

      {!locked && (
        <div className="flex items-center gap-2 pt-1">
          {editing ? (
            <>
              <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-brand-red text-white text-xs font-medium disabled:opacity-50">
                <Check size={13} /> 保存
              </button>
              <button type="button" onClick={() => setEditing(false)} className="px-2.5 py-1 rounded border border-neutral-line text-xs text-neutral-sub">キャンセル</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => onDecision(row.result_id, 'approved')} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-success-text text-white text-xs font-medium disabled:opacity-50">
                <Check size={13} /> 承認
              </button>
              <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-neutral-line text-xs text-neutral-text">
                <Pencil size={13} /> 修正して承認
              </button>
              {/* デザインレビュー反映 (2026-08-02): 「修正して承認」との誤操作を防ぐため warning トークンで視覚的に区別 (スキップ=対象から除外する準破壊的操作) */}
              <button type="button" onClick={() => onDecision(row.result_id, 'skipped')} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-warning-border text-xs text-warning-text disabled:opacity-50">
                <X size={13} /> スキップ
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function AiOcrReviewPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [commitConfirmOpen, setCommitConfirmOpen] = useState(false);

  const { data: job } = useQuery<OcrJobDetail>({
    queryKey: ['ai-ocr-job', jobId],
    queryFn: () => apiFetch<OcrJobDetail>(`/ai/ocr/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'queued' || s === 'processing' ? 3000 : false;
    },
  });

  const { data: pageResults = [] } = useQuery<PageResults[]>({
    queryKey: ['ai-ocr-results', jobId],
    queryFn: () => apiFetch<PageResults[]>(`/ai/ocr/jobs/${jobId}/results`),
    enabled: Boolean(jobId) && (job?.status === 'pending_review' || job?.status === 'completed'),
  });

  const { data: ingredientsAll = [] } = useQuery<Ingredient[]>({
    queryKey: ['ingredients'],
    queryFn: () => apiFetch<Ingredient[]>('/ingredients'),
    enabled: Boolean(job),
  });
  const ingredients = useMemo(() => ingredientsAll.filter((i) => i.store_id === job?.store_id), [ingredientsAll, job]);

  useEffect(() => {
    if (!selectedPageId && pageResults.length > 0) setSelectedPageId(pageResults[0]!.page_id);
  }, [pageResults, selectedPageId]);

  const decisionMut = useMutation({
    mutationFn: (params: { resultId: string; hitl_decision: 'approved' | 'corrected' | 'skipped'; corrected_value?: Record<string, unknown> }) =>
      authFetchJson(`${API_BASE}/ai/ocr/results/${params.resultId}`, {
        method: 'PATCH',
        body: JSON.stringify({ hitl_decision: params.hitl_decision, corrected_value: params.corrected_value }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-ocr-results', jobId] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : '保存に失敗しました'),
  });

  const commitMut = useMutation({
    mutationFn: () => authFetchJson<{ committed_count: number; skipped: Array<{ result_id: string; reason: string }>; job_completed: boolean }>(
      `${API_BASE}/ai/ocr/jobs/${jobId}/commit`, { method: 'POST' },
    ),
    onSuccess: (data) => {
      setCommitConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ['ai-ocr-results', jobId] });
      qc.invalidateQueries({ queryKey: ['ai-ocr-job', jobId] });
      qc.invalidateQueries({ queryKey: ['ai-ocr-jobs'] });
      toast.success(`${data.committed_count} 件を納品明細へ反映しました${data.skipped.length > 0 ? ` (${data.skipped.length} 件は未反映)` : ''}`);
    },
    onError: (e) => { setCommitConfirmOpen(false); toast.error(e instanceof Error ? e.message : '確定に失敗しました'); },
  });

  if (!job) {
    return <div className="text-sm text-neutral-sub">読込中…</div>;
  }

  if (job.status === 'uploaded' || job.status === 'queued' || job.status === 'processing') {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/ai-ocr" className="text-sm text-neutral-sub inline-flex items-center gap-1 hover:text-brand-red"><ArrowLeft size={14} /> 一覧に戻る</Link>
        <div className="p-8 rounded-xl border border-zinc-100 bg-white shadow-sm text-center space-y-3">
          <Loader2 className="animate-spin mx-auto text-brand-red" />
          <div className="text-sm text-neutral-text">AIが納品書を解析しています… ({job.page_count ?? '?'} ページ)</div>
          <div className="text-xs text-neutral-sub">解析が完了すると自動でレビュー画面に切り替わります</div>
        </div>
      </div>
    );
  }

  if (job.status === 'failed') {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/ai-ocr" className="text-sm text-neutral-sub inline-flex items-center gap-1 hover:text-brand-red"><ArrowLeft size={14} /> 一覧に戻る</Link>
        <div className="p-6 rounded-xl border border-red-100 bg-red-50 text-red-700 text-sm">
          解析に失敗しました: {job.error_message ?? '不明なエラー'}
        </div>
      </div>
    );
  }

  const selectedPage = pageResults.find((p) => p.page_id === selectedPageId);
  const totalRows = pageResults.reduce((sum, p) => sum + p.results.length, 0);
  const undecidedRows = pageResults.reduce((sum, p) => sum + p.results.filter((r) => !r.hitl_decision && !r.delivery_detail_id).length, 0);
  const committedRows = pageResults.reduce((sum, p) => sum + p.results.filter((r) => r.delivery_detail_id).length, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/ai-ocr" className="text-sm text-neutral-sub inline-flex items-center gap-1 hover:text-brand-red"><ArrowLeft size={14} /> 一覧に戻る</Link>
        <div className="flex items-center gap-3 text-sm text-neutral-sub">
          <span>{committedRows}/{totalRows} 件 確定済み</span>
          {isAdmin && job.status === 'pending_review' && (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={() => setCommitConfirmOpen(true)}
                disabled={undecidedRows > 0 || commitMut.isPending}
                className="px-3 py-1.5 rounded bg-brand-red text-white text-sm font-medium disabled:opacity-50"
              >
                確定して納品明細へ反映
              </button>
              {/* デザインレビュー反映 (2026-08-02): title属性はホバー依存でタッチデバイスで見えないため、常時表示のテキストに変更 */}
              {undecidedRows > 0 && (
                <span className="text-xs text-warning-text">未レビューの行が {undecidedRows} 件あります</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[120px_1fr_1fr] gap-4">
        {/* ページ一覧 */}
        <div className="space-y-1 max-h-[70vh] overflow-y-auto">
          {pageResults.map((p) => {
            const pending = p.results.filter((r) => !r.hitl_decision && !r.delivery_detail_id).length;
            return (
              <button
                key={p.page_id}
                type="button"
                onClick={() => setSelectedPageId(p.page_id)}
                className={`w-full text-left px-3 py-2 rounded text-sm ${selectedPageId === p.page_id ? 'bg-brand-red text-white' : 'bg-white border border-zinc-100 text-neutral-text hover:bg-zinc-50'}`}
              >
                {p.page_number}ページ
                {pending > 0 && <span className={`ml-1 text-xs ${selectedPageId === p.page_id ? 'text-white/80' : 'text-amber-600'}`}>({pending})</span>}
              </button>
            );
          })}
        </div>

        {/* 画像プレビュー */}
        <div className="border border-zinc-100 rounded-xl bg-white shadow-sm p-2 max-h-[70vh] overflow-auto">
          {selectedPage && jobId && <PageImage jobId={jobId} pageId={selectedPage.page_id} />}
        </div>

        {/* 抽出結果 */}
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          {selectedPage?.results.length === 0 && (
            <div className="text-sm text-zinc-400 p-4 text-center">このページに明細行はありません</div>
          )}
          {selectedPage?.results.map((row) => (
            <EditableRow
              key={row.result_id}
              row={row}
              ingredients={ingredients}
              saving={decisionMut.isPending}
              onDecision={(resultId, decision) => decisionMut.mutate({ resultId, hitl_decision: decision })}
              onSave={(resultId, correctedValue) => decisionMut.mutate({ resultId, hitl_decision: 'corrected', corrected_value: correctedValue })}
            />
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={commitConfirmOpen}
        title="納品明細へ反映"
        message="承認・修正済みの明細を delivery_details へ登録します。よろしいですか？"
        confirmLabel="反映する"
        loading={commitMut.isPending}
        onConfirm={() => commitMut.mutate()}
        onClose={() => setCommitConfirmOpen(false)}
      />
    </div>
  );
}
